import type { PostgrestError } from '@supabase/supabase-js';
import type { WriteResult } from '@/shared/lib/store-write';

export const API_URL: string = (import.meta.env.VITE_API_URL ?? '').replace(/\/+$/, '');

export type ApiMethod = 'POST' | 'PATCH' | 'DELETE';

interface ApiErrorBody {
  code?: string;
  message?: string;
  database_code?: string | null;
  database_message?: string | null;
  rule?: string;
}

export function said(error: PostgrestError, text: string): boolean {
  return `${error.message} ${error.details ?? ''}`.includes(text);
}

function refusal(status: number, body: ApiErrorBody): PostgrestError {
  return {
    name: 'ApiError',
    code: body.database_code ?? body.code ?? String(status),
    message: body.message ?? `The server answered ${status}.`,
    details: body.database_message ?? '',
    hint: body.rule ?? '',
  } as PostgrestError;
}

export interface RequestContext {
  headers(): Record<string, string>;
  wrote(method: ApiMethod, path: string, body: Record<string, unknown>, stored: unknown): void;
}

let requestContext: RequestContext | null = null;
const inFlight = new Set<Promise<unknown>>();

export function setRequestContext(context: RequestContext | null): void {
  requestContext = context;
}

export async function writesSettled(): Promise<void> {
  for (let pass = 0; pass < 20; pass += 1) {
    await new Promise((resolve) => setTimeout(resolve, 0));
    if (inFlight.size === 0) return;
    await Promise.allSettled([...inFlight]);
  }
}

export function apiPost(
  accessToken: () => Promise<string | null>,
  path: string,
  body: Record<string, unknown>,
  method: ApiMethod = 'POST',
): Promise<WriteResult> {
  const request = sendRequest(accessToken, path, body, method, requestContext);
  const done = () => { inFlight.delete(request); };
  inFlight.add(request);
  request.then(done, done);
  return request;
}

async function sendRequest(
  accessToken: () => Promise<string | null>,
  path: string,
  body: Record<string, unknown>,
  method: ApiMethod,
  context: RequestContext | null,
): Promise<WriteResult> {
  if (!API_URL) throw new Error('VITE_API_URL is not set, so there is no API to send this to.');
  const token = await accessToken();
  if (!token) throw new Error('There is no signed-in session to send this as.');

  const response = await fetch(`${API_URL}${path}`, {
    method,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...context?.headers() },
    body: JSON.stringify(body),
  });
  const payload = await response.json().catch(() => ({})) as Record<string, unknown>;
  if (!response.ok) {
    return { data: null, error: refusal(response.status, (payload.error ?? {}) as ApiErrorBody) };
  }
  const data = Object.values(payload);
  context?.wrote(method, path, body, data[0]);
  return { data, error: null };
}
